import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ArrowUpRightSquare, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useEncounter } from '@/contexts/EncounterContext';
import {
  problemsService, PatientCondition,
} from '@/services/problemsService';
import { parseApiError } from '@/lib/apiErrors';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';
import DiagnosisCombobox, { type DiagnosisPick } from './DiagnosisCombobox';

const EncounterDiagnosesPanel: React.FC = () => {
  const { clinicId, patientId, encounterId, isSigned } = useEncounter();
  const qc = useQueryClient();

  const [pick, setPick] = useState<DiagnosisPick | null>(null);
  const [codeOverride, setCodeOverride] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data: conditions = [], isLoading } = useQuery({
    queryKey: cacheKeys.patientConditions(patientId),
    queryFn: () => problemsService.getPatientConditions(clinicId, patientId),
  });

  const encounterDx = conditions.filter(c => c.category === 'encounter-diagnosis');
  const problemList = conditions.filter(c => c.category === 'problem-list-item' && c.status === 'active');

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: cacheRoots.patientConditions(patientId) });

  const effectiveCode = (codeOverride.trim() || pick?.snomedCode || '').trim();

  const addDiagnosis = async () => {
    if (!pick?.display?.trim()) {
      toast({
        title: 'Pick a diagnosis',
        description: 'Search for a diagnosis or type a free-text name.',
        variant: 'destructive',
      });
      return;
    }
    if (!effectiveCode) {
      toast({
        title: 'SNOMED code required',
        description: 'This diagnosis has no SNOMED code yet — enter one to continue.',
        variant: 'destructive',
      });
      return;
    }
    setIsAdding(true);
    try {
      await problemsService.createEncounterDiagnosis(clinicId, encounterId, {
        snomedCode: effectiveCode,
        display: pick.display.trim(),
      });
      setPick(null);
      setCodeOverride('');
      await invalidate();
      toast({ title: 'Diagnosis added', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Failed to add diagnosis', description: e.message, variant: 'destructive' });
    } finally {
      setIsAdding(false);
    }
  };

  const promote = async (id: string) => {
    setBusyId(id);
    try {
      await problemsService.promoteToProblemList(clinicId, id);
      await invalidate();
      toast({ title: 'Promoted to problem list', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Promote failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const resolve = async (id: string) => {
    setBusyId(id);
    try {
      await problemsService.resolveCondition(clinicId, id);
      await invalidate();
      toast({ title: 'Diagnosis resolved', variant: 'success' });
    } catch (err) {
      const e = parseApiError(err);
      toast({ title: 'Resolve failed', description: e.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='grid gap-6 md:grid-cols-3'>
      <div className='md:col-span-2 space-y-4'>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Today's diagnoses</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {!isSigned && (
              <div className='grid gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_140px_auto]'>
                <div className='min-w-0'>
                  <Label className='text-xs'>Diagnosis</Label>
                  <DiagnosisCombobox
                    value={pick}
                    onChange={(next) => {
                      setPick(next);
                      setCodeOverride('');
                    }}
                    placeholder='Search by name, SNOMED, or ICD-10…'
                  />
                </div>
                <div>
                  <Label htmlFor='dx-code' className='text-xs'>
                    SNOMED code
                    {pick?.snomedCode && !codeOverride && (
                      <span className='ml-1 text-[10px] font-normal text-slate-500'>(auto)</span>
                    )}
                  </Label>
                  <Input
                    id='dx-code'
                    placeholder={pick?.snomedCode || '44054006'}
                    value={codeOverride || pick?.snomedCode || ''}
                    onChange={e => setCodeOverride(e.target.value)}
                  />
                </div>
                <div className='flex items-end'>
                  <Button
                    onClick={addDiagnosis}
                    disabled={isAdding || !pick?.display?.trim()}
                    className='w-full'
                  >
                    {isAdding ? <Loader2 className='h-4 w-4 animate-spin' /> : <><Plus className='mr-1 h-4 w-4' />Add</>}
                  </Button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className='flex justify-center py-4'><Loader2 className='h-5 w-5 animate-spin text-slate-400' /></div>
            ) : encounterDx.length === 0 ? (
              <p className='text-sm text-slate-500'>No diagnoses recorded for this visit yet.</p>
            ) : (
              <ul className='divide-y divide-slate-100'>
                {encounterDx.map(c => (
                  <DiagnosisRow
                    key={c.id}
                    condition={c}
                    onPromote={() => promote(c.id)}
                    onResolve={() => resolve(c.id)}
                    busy={busyId === c.id}
                    locked={isSigned}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Active problem list</CardTitle>
        </CardHeader>
        <CardContent>
          {problemList.length === 0 ? (
            <p className='text-sm text-slate-500'>No active problems.</p>
          ) : (
            <ul className='space-y-2'>
              {problemList.map(p => (
                <li key={p.id} className='text-sm'>
                  <div className='font-medium text-slate-900'>{p.display}</div>
                  <div className='text-xs text-slate-500'>{p.code}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const DiagnosisRow: React.FC<{
  condition: PatientCondition;
  onPromote: () => void;
  onResolve: () => void;
  busy: boolean;
  locked: boolean;
}> = ({ condition, onPromote, onResolve, busy, locked }) => (
  <li className='flex items-start justify-between py-3'>
    <div>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-slate-900'>{condition.display}</span>
        <Badge variant='outline' className='text-[10px]'>{condition.code}</Badge>
        <Badge
          variant='outline'
          className={
            condition.status === 'resolved'
              ? 'border-slate-200 bg-slate-50 text-slate-500 text-[10px]'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]'
          }
        >
          {condition.status}
        </Badge>
      </div>
      <div className='mt-0.5 text-xs text-slate-500'>
        Recorded {new Date(condition.recordedDate).toLocaleDateString('en-US')}
      </div>
    </div>
    {!locked && (
      <div className='flex gap-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={onPromote}
          disabled={busy}
          title='Promote to problem list'
        >
          <ArrowUpRightSquare className='mr-1 h-3 w-3' />
          To problem list
        </Button>
        {condition.status !== 'resolved' && (
          <Button
            variant='ghost'
            size='sm'
            onClick={onResolve}
            disabled={busy}
            title='Mark resolved'
          >
            <Check className='mr-1 h-3 w-3' />
            Resolve
          </Button>
        )}
      </div>
    )}
  </li>
);

export default EncounterDiagnosesPanel;
